import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  query, 
  where, 
  orderBy, 
  onSnapshot, 
  Timestamp, 
  getDocs, 
  getDoc,
  limit,
} from 'firebase/firestore';
import { db } from '@/firebase';
import { 
  ChatParticipant, 
  ChatMessage, 
  ChatThread, 
  ChatThreadSummary, 
  CreateThreadRequest,
  ChatAttachment
} from '@/types/chat';
import { User } from '@/types/users';
import { isDemoWorkspace } from '@/utils/apiHelpers';
import { loadMockData } from '@/mock';
import { sendChatMessageNotificationToUser } from './messaging';
import { checkAndCreateDailyMessageNotification, createNotification, resolveTargetAuthUidsFromUserDocIds } from './notifications';

// Create a new chat thread
export const createChatThread = async (
  request: CreateThreadRequest,
  currentUser: User,
  firestoreUser?: User,
  participantDetails?: ChatParticipant[] // Allow passing pre-fetched participant details
): Promise<string> => {
  try {
    console.log('Creating chat thread with request:', request);
    console.log('Current user:', currentUser);
    console.log('Firestore user:', firestoreUser);
    console.log('Pre-fetched participant details:', participantDetails);
    
    // Check if this is a demo workspace
    if (firestoreUser && isDemoWorkspace(firestoreUser)) {
      console.log('Demo workspace detected, returning demo thread ID');
      return `demo-thread-${Date.now()}`;
    }

    console.log('Not a demo workspace, proceeding with Firestore operations');
    console.log('Workspace info:', firestoreUser?.workspace);
    console.log('Workspace slug:', firestoreUser?.workspace?.slug);

    // Validate input parameters
    if (!request.workspaceId) {
      throw new Error('Workspace ID is required');
    }
    if (!request.participants || request.participants.length === 0) {
      throw new Error('Participants array cannot be empty');
    }
    if (!currentUser.workspace?.id) {
      throw new Error('Current user workspace ID is required');
    }

    // Ensure current user is included in participants
    const allParticipants = request.participants.includes(currentUser.id) 
      ? request.participants 
      : [...request.participants, currentUser.id];

    // Use pre-fetched participant details if provided, otherwise fetch them
    let finalParticipantDetails: ChatParticipant[] = [];
    
    if (participantDetails && participantDetails.length > 0) {
      console.log('Using pre-fetched participant details');
      finalParticipantDetails = participantDetails;
    } else {
      console.log('Fetching participant details from database');
      
      // Add current user details
      finalParticipantDetails.push({
        userId: currentUser.id,
        username: currentUser.displayName || currentUser.email || 'Current User',
        profilePicture: currentUser.profilePicture?.url
      });
      
      // Add other participant details by fetching from users collection
      for (const participantId of request.participants) {
        if (participantId !== currentUser.id) {
          try {
            // Try to fetch user details from users collection
            const userDoc = await getDoc(doc(db, 'users', participantId));
            if (userDoc.exists()) {
              const userData = userDoc.data() as any;
              finalParticipantDetails.push({
                userId: participantId,
                username: userData.displayName || userData.email || `User ${participantId.slice(0, 8)}`,
                profilePicture: userData.profilePicture?.url
              });
            } else {
              // Fallback if user document doesn't exist
              finalParticipantDetails.push({
                userId: participantId,
                username: `User ${participantId.slice(0, 8)}`,
                profilePicture: undefined
              });
            }
          } catch (error) {
            console.warn('Could not fetch user details for participant:', participantId, error);
            // Fallback if fetch fails
            finalParticipantDetails.push({
              userId: participantId,
              username: `User ${participantId.slice(0, 8)}`,
              profilePicture: undefined
            });
          }
        }
      }
    }

    const threadData: Omit<ChatThread, 'id'> = {
      participants: allParticipants,
      participantDetails: finalParticipantDetails, // Store participant details
      lastMessage: {
        text: request.initialMessage || 'Thread started',
        timestamp: Timestamp.now(),
        fromUserId: currentUser.id
      },
      workspace: {
        id: request.workspaceId,
        name: currentUser.workspace?.name || 'Unknown Workspace',
        slug: currentUser.workspace?.slug || 'unknown'
      },
      updatedAt: Timestamp.now(),
      isGroupChat: request.isGroupChat || allParticipants.length > 2
    };

    // Only add group fields if they have values
    if (request.groupName) {
      (threadData as any).groupName = request.groupName;
    }
    if (request.groupAvatar) {
      (threadData as any).groupAvatar = request.groupAvatar;
    }

    // Final validation and logging
    console.log('Thread data to be created:', JSON.stringify(threadData, null, 2));
    
    // Check for any undefined values and remove them before sending to Firestore
    const cleanThreadData = JSON.parse(JSON.stringify(threadData, (_key, value) => {
      if (value === undefined) {
        return null; // Convert undefined to null for Firestore
      }
      return value;
    }));
    
    console.log('Cleaned thread data:', JSON.stringify(cleanThreadData, null, 2));
    
    // Final validation
    if (!cleanThreadData.participants || cleanThreadData.participants.length === 0) {
      throw new Error('Participants array cannot be empty');
    }
    if (!cleanThreadData.workspace?.id) {
      throw new Error('Workspace ID is required');
    }

    const docRef = await addDoc(collection(db, 'chatThreads'), cleanThreadData);

    // If there's an initial message, add it to the messages subcollection
    if (request.initialMessage) {
      try {
        await addDoc(
          collection(db, 'chatThreads', docRef.id, 'messages'),
          {
            message: request.initialMessage,
            fromUserId: currentUser.id,
            timestamp: Timestamp.now(),
            isRead: false
          }
        );
      } catch (messageError) {
        console.warn('Could not add initial message to subcollection:', messageError);
        // Continue anyway - the thread was created successfully
      }
    }

    // Create a notification for new thread creation
    try {
      const otherParticipants = allParticipants.filter(participantId => participantId !== currentUser.id);
      
      if (otherParticipants.length > 0) {
        const threadTitle = request.isGroupChat 
          ? (request.groupName || 'Group Chat')
          : finalParticipantDetails.find(p => p.userId !== currentUser.id)?.username || 'Someone';

        const targetAuthUids = await resolveTargetAuthUidsFromUserDocIds(otherParticipants);
        if (targetAuthUids.length > 0) {
          await createNotification({
            type: 'message',
            title: `New conversation started with ${threadTitle}`,
            priority: 'medium',
            targetUsers: otherParticipants,
            targetAuthUids,
            actionUrl: `/residents/messages?threadId=${docRef.id}`,
            sender: {
              id: currentUser.id,
              name: currentUser.displayName || currentUser.email || 'Someone'
            }
          }, currentUser);
        }
      }
    } catch (notificationError) {
      console.warn('Failed to create new thread notification:', notificationError);
      // Don't fail thread creation if notification fails
    }

    return docRef.id;
  } catch (error: any) {
    console.error('Error creating chat thread:', error);
    
    // If it's a permissions error, suggest using demo mode
    if (error.code === 'permission-denied' || error.message?.includes('permissions')) {
      console.warn('Firebase permissions error. Consider using demo workspace or checking Firestore rules.');
      throw new Error('Insufficient permissions to create chat thread. Please check your workspace access.');
    }
    
    throw error;
  }
};

// Get all chat threads for a user in a workspace
export const getUserChatThreads = (
  currentUserId: string,
  workspaceId: string,
  callback: (threads: ChatThreadSummary[]) => void,
  firestoreUser?: User
) => {
  // Check if this is a demo workspace
  console.log('Checking if demo workspace, firestoreUser:', firestoreUser);
  console.log('isDemoWorkspace result:', firestoreUser ? isDemoWorkspace(firestoreUser) : 'no user');
  
  if (firestoreUser && isDemoWorkspace(firestoreUser)) {
    // For demo workspaces, use mock data
    console.log('Loading mock data for demo workspace, language:', firestoreUser.language);
    loadMockData('residentContacts', firestoreUser.language).then((mockContacts: any) => {
      console.log('Mock contacts loaded:', mockContacts);
      const mockThreads: ChatThreadSummary[] = mockContacts.map((contact: any) => ({
        id: `demo-${contact.id}`,
        participants: [
          {
            userId: currentUserId,
            username: firestoreUser.displayName || 'Current User',
            profilePicture: firestoreUser.profilePicture?.url
          },
          {
            userId: contact.id,
            username: contact.name,
            profilePicture: contact.avatarUrl
          }
        ],
        lastMessage: {
          id: `demo-msg-${contact.id}`,
          message: contact.lastMessage || 'No messages yet',
          fromUserId: contact.lastMessage ? contact.id : currentUserId,
          timestamp: contact.lastMessageTime ? Timestamp.fromDate(contact.lastMessageTime.toDate()) : Timestamp.now()
        },
        unreadCount: contact.unreadCount || 0,
        isGroupChat: false
      }));
      
      console.log('Mock threads created:', mockThreads);
      callback(mockThreads);
    });
    
    return () => {};
  }

  // Query for threads where current user is a participant
  // Removed orderBy to avoid expensive composite index - we'll sort client-side
  const threadsQuery = query(
    collection(db, 'chatThreads'),
    where('workspace.id', '==', workspaceId),
    where('participants', 'array-contains', currentUserId)
  );

  return onSnapshot(threadsQuery, async (snapshot) => {
    const threads: ChatThreadSummary[] = [];
    
    for (const docSnapshot of snapshot.docs) {
      try {
        const threadData = docSnapshot.data() as ChatThread;
        
        // Get participant details
        const participantDetails: ChatParticipant[] = [];
        
        // Use stored participant details if available
        if (threadData.participantDetails && threadData.participantDetails.length > 0) {
          participantDetails.push(...threadData.participantDetails);
        } else {
          // Fallback: build participant details from stored data
          for (const participantId of threadData.participants) {
            if (participantId === currentUserId) {
              // Use current user data
              participantDetails.push({
                userId: participantId,
                username: firestoreUser?.displayName || 'Current User',
                profilePicture: firestoreUser?.profilePicture?.url
              });
            } else {
              // Try to get from users collection or use default
              try {
                const userDoc = await getDoc(doc(db, 'users', participantId));
                if (userDoc.exists()) {
                  const userData = userDoc.data() as any;
                  participantDetails.push({
                    userId: participantId,
                    username: userData.displayName || userData.email || `User ${participantId.slice(0, 8)}`,
                    profilePicture: userData.profilePicture?.url
                  });
                } else {
                  participantDetails.push({
                    userId: participantId,
                    username: `User ${participantId.slice(0, 8)}`,
                    profilePicture: undefined
                  });
                }
              } catch (error) {
                console.warn('Could not fetch user details for participant:', participantId, error);
                participantDetails.push({
                  userId: participantId,
                  username: `User ${participantId.slice(0, 8)}`,
                  profilePicture: undefined
                });
              }
            }
          }
        }

        // Calculate unread count (messages not from current user and not read)
        let unreadCount = 0;
        try {
          const unreadQuery = query(
            collection(db, 'chatThreads', docSnapshot.id, 'messages'),
            where('fromUserId', '!=', currentUserId),
            where('isRead', '==', false)
          );
          
          const unreadSnapshot = await getDocs(unreadQuery);
          unreadCount = unreadSnapshot.size;
        } catch (error) {
          console.warn('Could not calculate unread count for thread:', docSnapshot.id, error);
        }

        // Get last message
        let lastMessage = {
          id: '',
          message: 'No messages yet',
          fromUserId: currentUserId,
          timestamp: Timestamp.now()
        };
        
        try {
          const lastMessageQuery = query(
            collection(db, 'chatThreads', docSnapshot.id, 'messages'),
            orderBy('timestamp', 'desc'),
            limit(1)
          );
          
          const lastMessageSnapshot = await getDocs(lastMessageQuery);
          if (!lastMessageSnapshot.empty) {
            const lastMsgData = lastMessageSnapshot.docs[0].data();
            lastMessage = {
              id: lastMessageSnapshot.docs[0].id,
              message: lastMsgData.message || '',
              fromUserId: lastMsgData.fromUserId || '',
              timestamp: lastMsgData.timestamp || Timestamp.now()
            };
          }
        } catch (error) {
          console.warn('Could not fetch last message for thread:', docSnapshot.id, error);
        }

        threads.push({
          id: docSnapshot.id,
          participantDetails, // Changed from participants to participantDetails
          lastMessage,
          unreadCount,
          isGroupChat: threadData.isGroupChat || false,
          groupName: threadData.groupName,
          groupAvatar: threadData.groupAvatar
        });
      } catch (error) {
        console.error('Error processing thread:', error);
      }
    }
    
    // Sort threads by last message timestamp descending (most recent first) - client-side sorting
    threads.sort((a, b) => {
      if (!a.lastMessage?.timestamp && !b.lastMessage?.timestamp) return 0;
      if (!a.lastMessage?.timestamp) return 1; // Threads without last message go to end
      if (!b.lastMessage?.timestamp) return -1; // Threads without last message go to end
      
      return b.lastMessage.timestamp.toMillis() - a.lastMessage.timestamp.toMillis();
    });
    
    callback(threads);
  }, (error) => {
    console.error('Error in chatThreads onSnapshot:', error);
    
    // If it's a permissions error, suggest using demo mode
    if (error.code === 'permission-denied' || error.message?.includes('permissions')) {
      console.warn('Firebase permissions error. Consider using demo workspace or checking Firestore rules.');
      // Return empty threads instead of crashing
      callback([]);
    } else {
      // For other errors, also return empty threads
      callback([]);
    }
  });
};

// Get messages for a specific thread
export const getThreadMessages = (
  threadId: string,
  callback: (messages: ChatMessage[]) => void,
  limitCount: number = 50,
  firestoreUser?: User
) => {
  // Check if this is a demo workspace
  if (firestoreUser && isDemoWorkspace(firestoreUser)) {
    // For demo workspaces, use mock data
    loadMockData('chatHistory', firestoreUser.language).then((mockChatHistory: any) => {
      // Find the first contact's chat history as demo
      const firstContactId = Object.keys(mockChatHistory)[0];
      const mockMessages = mockChatHistory[firstContactId] || [];
      
      const convertedMessages: ChatMessage[] = mockMessages.map((msg: any, _index: number) => ({
        id: msg.id,
        message: msg.text,
        fromUserId: msg.senderId === 'currentUser' ? firestoreUser.id : msg.senderId,
        timestamp: Timestamp.fromDate(msg.timestamp),
        isRead: msg.isRead || false
      }));
      
      callback(convertedMessages);
    });
    
    return () => {};
  }

  const messagesQuery = query(
    collection(db, 'chatThreads', threadId, 'messages'),
    orderBy('timestamp', 'desc'),
    limit(limitCount)
  );

  return onSnapshot(messagesQuery, (snapshot) => {
    const messages: ChatMessage[] = [];
    snapshot.forEach((doc) => {
      const messageData = doc.data();
      messages.push({
        id: doc.id,
        ...messageData
      } as ChatMessage);
    });
    
    // Sort by timestamp ascending for display
    messages.sort((a, b) => {
      if (!a.timestamp && !b.timestamp) return 0;
      if (!a.timestamp) return 1;
      if (!b.timestamp) return -1;
      return a.timestamp.toMillis() - b.timestamp.toMillis();
    });
    
    callback(messages);
  }, (error) => {
    console.error('Error listening to thread messages:', error);
    callback([]);
  });
};

// Send a message to a specific thread
export const sendMessageToThread = async (
  threadId: string,
  message: string,
  fromUserId: string,
  firestoreUser?: User,
  attachments?: ChatAttachment[]
): Promise<string> => {
  try {
    // Check if this is a demo workspace
    if (firestoreUser && isDemoWorkspace(firestoreUser)) {
      return `demo-message-${Date.now()}`;
    }

    // Check if thread exists
    const threadDoc = await getDoc(doc(db, 'chatThreads', threadId));
    if (!threadDoc.exists()) {
      throw new Error(`Thread ${threadId} does not exist`);
    }

    // Create message data
    const messageData = {
      message,
      fromUserId,
      timestamp: Timestamp.now(),
      isRead: false,
      ...(attachments && attachments.length > 0 && { attachments })
    };

    // Add message to subcollection
    const docRef = await addDoc(
      collection(db, 'chatThreads', threadId, 'messages'),
      messageData
    );

    // Update thread's last message and timestamp
    await updateDoc(doc(db, 'chatThreads', threadId), {
      lastMessage: {
        text: message,
        timestamp: messageData.timestamp,
        fromUserId
      },
      updatedAt: messageData.timestamp
    });

    // Send FCM notification to other participants
    try {
      const threadData = threadDoc.data() as ChatThread;
      if (threadData && threadData.participants) {
        // Check if this is the first message of the day and create notification
        if (firestoreUser) {
          try {
            await checkAndCreateDailyMessageNotification(
              threadId,
              {
                id: docRef.id,
                message,
                fromUserId,
                timestamp: messageData.timestamp,
                isRead: false,
                attachments
              } as ChatMessage,
              threadData,
              firestoreUser
            );
          } catch (dailyNotificationError) {
            console.warn('Failed to create daily message notification:', dailyNotificationError);
            // Don't fail the message sending if daily notification fails
          }
        }

        // Get other participants (excluding sender)
        const otherParticipants = threadData.participants.filter((participantId: string) => participantId !== fromUserId);
        
        for (const participantId of otherParticipants) {
          try {
            const senderName = firestoreUser?.displayName || firestoreUser?.email || 'Unknown User';
            
            // Send notification to all user's devices
            await sendChatMessageNotificationToUser(
              participantId,
              senderName,
              message,
              threadId,
              docRef.id,
              fromUserId
            );
            
          } catch (userError) {
            console.warn(`Failed to send FCM notification to user ${participantId}:`, userError);
          }
        }
      }
    } catch (fcmError) {
      console.warn('Failed to send FCM notification:', fcmError);
      // Don't fail the message sending if FCM fails
    }

    return docRef.id;
  } catch (error) {
    console.error('Error sending message to thread:', error);
    throw error;
  }
};

// Mark messages as read in a thread
export const markThreadMessagesAsRead = async (
  threadId: string,
  currentUserId: string,
  firestoreUser?: User
): Promise<void> => {
  try {
    // Check if this is a demo workspace
    if (firestoreUser && isDemoWorkspace(firestoreUser)) {
      return;
    }

    // Get unread messages from other users
    const unreadQuery = query(
      collection(db, 'chatThreads', threadId, 'messages'),
      where('fromUserId', '!=', currentUserId),
      where('isRead', '==', false)
    );
    
    const unreadSnapshot = await getDocs(unreadQuery);
    const updatePromises = unreadSnapshot.docs.map(doc =>
      updateDoc(doc.ref, { isRead: true })
    );
    
    await Promise.all(updatePromises);
  } catch (error: any) {
    console.error('Error marking thread messages as read:', error);
    
    // If it's a permissions error, log it but don't crash
    if (error.code === 'permission-denied' || error.message?.includes('permissions')) {
      console.warn('Firebase permissions error in markThreadMessagesAsRead. Consider using demo workspace or checking Firestore rules.');
    }
  }
};

// Find or create a thread between two users
export const findOrCreateThread = async (
  participantIds: string[],
  workspaceId: string,
  currentUser: User,
  firestoreUser?: User
): Promise<string> => {
  try {
    console.log('findOrCreateThread called with:', { participantIds, workspaceId, currentUserId: currentUser.id });
    
    // Check if this is a demo workspace
    if (firestoreUser && isDemoWorkspace(firestoreUser)) {
      console.log('Demo workspace detected, returning demo thread ID');
      return `demo-thread-${Date.now()}`;
    }

    // Ensure current user is included
    const allParticipants = participantIds.includes(currentUser.id) 
      ? participantIds 
      : [...participantIds, currentUser.id];

    // Check if thread already exists
    const existingThreadQuery = query(
      collection(db, 'chatThreads'),
      where('workspace.id', '==', workspaceId),
      where('participants', 'array-contains', currentUser.id)
    );

    const existingSnapshot = await getDocs(existingThreadQuery);
    
    // Find exact match for participants
    for (const doc of existingSnapshot.docs) {
      const threadData = doc.data() as ChatThread;
      if (threadData.participants.length === allParticipants.length &&
          allParticipants.every(id => threadData.participants.includes(id))) {
        console.log('Existing thread found:', doc.id);
        return doc.id; // Return existing thread
      }
    }

    // Don't create thread immediately - return a temporary ID
    // Thread will be created when first message is sent
    console.log('No existing thread found, returning temporary ID');
    return `temp-thread-${Date.now()}-${currentUser.id}`;

  } catch (error) {
    console.error('Error finding or creating thread:', error);
    throw error;
  }
};

// One-time fetch for chat threads (no real-time listener)
export const getUserChatThreadsOnce = async (
  userId: string,
  workspaceId: string,
  user: any
): Promise<ChatThreadSummary[]> => {
  try {
    console.log('🔍 getUserChatThreadsOnce called with:', { userId, workspaceId });
    const threadsRef = collection(db, 'chatThreads');
    
    // First, try to find threads with the exact workspaceId field
    let q = query(
      threadsRef,
      where('participants', 'array-contains', userId),
      where('workspaceId', '==', workspaceId)
    );
    
    let snapshot = await getDocs(q);
    let threads: any[] = [];
    
    // If no results, try alternative field names
    if (snapshot.empty) {
      q = query(
        threadsRef,
        where('participants', 'array-contains', userId),
        where('workspace.id', '==', workspaceId)
      );
      
      snapshot = await getDocs(q);
    }
    
    // If still no results, try just participants (no workspace filter)
    if (snapshot.empty) {
      q = query(
        threadsRef,
        where('participants', 'array-contains', userId)
      );
      
      snapshot = await getDocs(q);
    }
    
    // Process each thread to populate required fields
    for (const docSnapshot of snapshot.docs) {
      try {
        const threadData = docSnapshot.data();
        
        // Get participant details
        const participantDetails: any[] = [];
        if (threadData.participants && Array.isArray(threadData.participants)) {
          for (const participantId of threadData.participants) {
            if (participantId === userId) {
              // Use current user data
              participantDetails.push({
                userId: participantId,
                username: user?.displayName || user?.email || 'Current User',
                profilePicture: user?.profilePicture?.url
              });
            } else {
              // Try to get from users collection or use default
              try {
                const userDoc = await getDoc(doc(db, 'users', participantId));
                if (userDoc.exists()) {
                  const userData = userDoc.data() as any;
                  participantDetails.push({
                    userId: participantId,
                    username: userData.displayName || userData.email || `User ${participantId.slice(0, 8)}`,
                    profilePicture: userData.profilePicture?.url
                  });
                } else {
                  participantDetails.push({
                    userId: participantId,
                    username: `User ${participantId.slice(0, 8)}`,
                    profilePicture: undefined
                  });
                }
              } catch (error) {
                console.warn('Could not fetch user details for participant:', participantId, error);
                participantDetails.push({
                  userId: participantId,
                  username: `User ${participantId.slice(0, 8)}`,
                  profilePicture: undefined
                });
              }
            }
          }
        }

        // Calculate unread count
        let unreadCount = 0;
        try {
          const unreadQuery = query(
            collection(db, 'chatThreads', docSnapshot.id, 'messages'),
            where('fromUserId', '!=', userId),
            where('isRead', '==', false)
          );
          
          const unreadSnapshot = await getDocs(unreadQuery);
          unreadCount = unreadSnapshot.size;
        } catch (error) {
          console.warn('Could not calculate unread count for thread:', docSnapshot.id, error);
        }

        // Get last message
        let lastMessage = {
          id: '',
          message: 'No messages yet',
          fromUserId: userId,
          timestamp: Timestamp.now()
        };
        
        try {
          const lastMessageQuery = query(
            collection(db, 'chatThreads', docSnapshot.id, 'messages'),
            orderBy('timestamp', 'desc'),
            limit(1)
          );
          
          const lastMessageSnapshot = await getDocs(lastMessageQuery);
          if (!lastMessageSnapshot.empty) {
            const lastMsgData = lastMessageSnapshot.docs[0].data();
            lastMessage = {
              id: lastMessageSnapshot.docs[0].id,
              message: lastMsgData.message || '',
              fromUserId: lastMsgData.fromUserId || '',
              timestamp: lastMsgData.timestamp || Timestamp.now()
            };
          }
        } catch (error) {
          console.warn('Could not fetch last message for thread:', docSnapshot.id, error);
        }

        threads.push({
          id: docSnapshot.id,
          participantDetails,
          lastMessage,
          unreadCount,
          isGroupChat: threadData.isGroupChat || false,
          groupName: threadData.groupName,
          groupAvatar: threadData.groupAvatar
        });
      } catch (error) {
        console.error('Error processing thread:', error);
      }
    }
    
    // Sort by timestamp if available
    if (threads.length > 0) {
      try {
        threads.sort((a, b) => {
          const aTime = a.lastMessage?.timestamp?.toMillis?.() || a.lastMessage?.timestamp?.seconds || 0;
          const bTime = b.lastMessage?.timestamp?.toMillis?.() || b.lastMessage?.timestamp?.seconds || 0;
          return bTime - aTime;
        });
      } catch (sortError) {
        console.warn('Could not sort threads by timestamp:', sortError);
      }
    }
    
    return threads;
  } catch (error) {
    console.error('❌ Error fetching chat threads:', error);
    throw error;
  }
};

// One-time fetch for thread messages (no real-time listener)
export const getThreadMessagesOnce = async (
  threadId: string,
  limitCount: number = 50,
  _user: any,
  beforeTimestamp?: any // For pagination - fetch messages before this timestamp
): Promise<ChatMessage[]> => {
  try {
    const messagesRef = collection(db, 'chatThreads', threadId, 'messages');
    
    let q;
    if (beforeTimestamp) {
      // For pagination - use where clause with timestamp for proper pagination
      q = query(
        messagesRef,
        where('timestamp', '<', beforeTimestamp),
        orderBy('timestamp', 'desc'),
        limit(limitCount)
      );
    } else {
      // Initial load - get latest messages
      q = query(
        messagesRef,
        orderBy('timestamp', 'desc'),
        limit(limitCount)
      );
    }
    
    const snapshot = await getDocs(q);
    let messages = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as ChatMessage[];
    
    // Reverse messages to show newest first (for initial load) or oldest first (for pagination)
    if (!beforeTimestamp) {
      // Initial load - reverse to show newest first
      messages = messages.reverse();
    }
    

    
    return messages;
  } catch (error) {
    console.error('Error fetching messages:', error);
    throw error;
  }
};
